/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * Licensed under The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */
import type { ComponentChildren, JSX } from 'preact';

import { gridInterpolations } from '../utils/UNSAFE_interpolations/grid';
import type { GridProps } from '../utils/UNSAFE_interpolations/grid';
import { boxAlignmentInterpolations } from '../utils/UNSAFE_interpolations/boxalignment';
import type { BoxAlignmentProps } from '../utils/UNSAFE_interpolations/boxalignment';
import { mergeInterpolations } from '../utils/UNSAFE_mergeInterpolations';
import { baseStyles } from './GridStyles.css';
import { TestIdProps, useTestId } from '#hooks/UNSAFE_useTestId';
import type { UnpackSignals } from '@oracle/oraclejet-internal-utilities/attributeUtils';

type Props = GridProps &
  BoxAlignmentProps &
  TestIdProps & {
    /**
     * The aria role for the Grid
     */
    role?: UnpackSignals<JSX.HTMLAttributes>['role'];
    /**
     * aria-label for this Grid
     */
    'aria-label'?: UnpackSignals<JSX.AriaAttributes>['aria-label'];
    /**
     * aria-hidden for this Grid
     */
    'aria-hidden'?: UnpackSignals<JSX.AriaAttributes>['aria-hidden'];
    /**
     * The children to put in the grid.
     */
    children?: ComponentChildren;
  };

const interpolations = [
  ...Object.values(gridInterpolations),
  ...Object.values(boxAlignmentInterpolations)
];

const styleInterpolations = mergeInterpolations<Props>(interpolations);

/**
 * An implicit grid. Resize your browser to see how items reflow.
 */
export const Grid = ({
  'aria-label': ariaLabel,
  'aria-hidden': ariaHidden,
  children,
  testId,
  role,
  ...props
}: Props) => {
  const { class: cls, ...styles } = styleInterpolations(props);
  const testIdProps = useTestId(testId);
  const allStyles = { ...styles };
  return (
    <div
      class={`${baseStyles} ${cls ? cls : ''}`}
      style={allStyles}
      role={role}
      aria-label={ariaLabel}
      aria-hidden={ariaHidden}
      {...testIdProps}>
      {children}
    </div>
  );
};
